import React, { Fragment } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { MuiThemeProvider, Typography, Divider } from '@material-ui/core';
import rehypeRaw from 'rehype-raw';
import ReactMarkdown from 'react-markdown';
import { BASE_URL, IMAGE_PATHS } from '../../../constants/paths.constants';
import CASE_STUDY, { CaseStudyFormat } from '../../../constants/caseStudy.constants';
import { axuminTheme, poslumaTheme, darkGrey, dividerGrey } from '../../../app/theme';

const useStyles = makeStyles(() => ({
  headerImage: {
    height: 77,
    marginBottom: 20,
  },
  heading: {
    fontSize: 20,
    fontWeight: 600,
    color: darkGrey,
  },
  divider: {
    marginTop: 10,
    marginBottom: 24,
    width: '100%',
    background: dividerGrey,
  },
  list: {
    marginTop: 0,
    marginBottom: 32,
    paddingLeft: 20,
  },
  text: {
    paddingBottom: 4,
  },
  markdownText: {
    fontSize: 18,
    color: darkGrey,
  },
  nestedList: {
    paddingLeft: 20,
    listStyleType: 'none',
  },
  nestedText: {
    fontSize: 17,
  },
  images: {
    display: 'block',
    width: '100%',
  },
}));

type FindingsTabProps = {
  module: string;
  page: number;
  index?: number;
  trainingId: number;
};

const FindingsTab = ({ module, trainingId, page, index }: FindingsTabProps): JSX.Element => {
  const classes = useStyles();
  const theme = trainingId === 1 ? axuminTheme : poslumaTheme;
  const caseStudyData =
    index !== undefined
      ? (CASE_STUDY[module][page.toString()] as CaseStudyFormat[])[index] || {}
      : (CASE_STUDY[module][page.toString()] as CaseStudyFormat) || {};
  const { findings } = caseStudyData;
  const caseStudyPath = IMAGE_PATHS.CASE_STUDY[trainingId.toString() as keyof typeof IMAGE_PATHS.CASE_STUDY];

  return (
    <MuiThemeProvider theme={theme}>
      <img
        className={classes.headerImage}
        src={`${BASE_URL}${caseStudyPath.CASE_STUDY_TAB_3_IMAGE.path}`}
        alt={`${caseStudyPath.CASE_STUDY_TAB_3_IMAGE.alt}`}
      />

      {findings.map((entry) => {
        return (
          <Fragment key={entry.sectionName}>
            <Typography className={classes.heading}>{entry.sectionName}</Typography>
            <Divider className={classes.divider} />
            <ul className={classes.list}>
              {entry.itemsList &&
                entry.itemsList.length > 0 &&
                entry.itemsList.map((item) => {
                  if (typeof item === 'string') {
                    if (trainingId === 2) {
                      return (
                        <li key={item}>
                          <ReactMarkdown className={classes.markdownText} rehypePlugins={[rehypeRaw]}>
                            {item}
                          </ReactMarkdown>
                        </li>
                      );
                    }
                    return (
                      <li key={item}>
                        <Typography variant="body1" className={classes.text}>
                          {item}
                        </Typography>
                      </li>
                    );
                  }
                  if (Array.isArray(item)) {
                    return (
                      <ul className={classes.nestedList} key={item[0]}>
                        {item.length > 0 &&
                          item.map((nestedItem) => (
                            <li key={nestedItem}>
                              <Typography variant="body1" className={classes.nestedText}>
                                {nestedItem}
                              </Typography>
                            </li>
                          ))}
                      </ul>
                    );
                  }
                  return (
                    <img
                      key={item.alt}
                      className={classes.images}
                      src={`${BASE_URL}${item.path}`}
                      alt={`${item.alt}`}
                    />
                  );
                })}
            </ul>
          </Fragment>
        );
      })}
    </MuiThemeProvider>
  );
};

export default FindingsTab;
